import React from 'react';

import { fetchAnalyticsHosts } from './api';
import type { HostSelection, WebsiteFilters } from './filters';
import { postTrafficQuality, QualityError } from './quality/api';
import { reportRequest } from './quality/compile';
import { qualityScreenState, type QualityScreenState } from './quality/errors';
import type { TrafficQualityMode } from './quality/gate';
import { reconcileHosts } from './quality/hosts';
import { enqueueQuality } from './quality/queue';
import type { QualityErrorCode, QualityReport, QualityReportRequest } from './quality/types';

/**
 * The Website screen's quality report. `idle`: quality mode is off or there is no brand (nothing asked). `no-site`:
 * the brand has no address. `no-selection`: no address is chosen, by the owner (`chosen`) or because the saved ones
 * are gone (`stale`); no request is made.
 */
export type QualityReportState = {
  status: 'idle' | 'loading' | 'ready' | 'no-site' | 'no-selection' | 'error';
  /** The figures to show: the answer to the current request, or an earlier answer to that same request (`held`). */
  report: QualityReport | null;
  /** The last report this brand and mode landed, for any request: its address rows feed the Filters sheet. */
  latest: QualityReport | null;
  /** `report` is an earlier read of the same request, kept during the busy retry or after a failed read (note 9). */
  held: boolean;
  /** What the screen shows for a failed read (status `error`). */
  error: QualityScreenState | null;
  noSelection: 'chosen' | 'stale' | null;
  reload: () => void;
};

type View = Omit<QualityReportState, 'reload'>;
const IDLE: View = { status: 'idle', report: null, latest: null, held: false, error: null, noSelection: null };
const OFF: QualityReportState = { ...IDLE, reload: () => undefined };

/** A view with what it answers (`ask`) and its brand and mode. */
type Shown = { view: View; ask: string; scope: string };

/**
 * What a read answers: the brand, the mode, the request (null: no address chosen; `wait`: it waits for the attribution
 * flag) and the reload count.
 */
type Ask = [
  tenantId: number | null,
  mode: TrafficQualityMode,
  request: QualityReportRequest | null | 'wait',
  nonce: number,
];
const askOf = (...ask: Ask): string => JSON.stringify(ask);
const scopeOf = (tenantId: number | null, mode: TrafficQualityMode): string => `${tenantId}:${mode}`;
/** What a held report must answer: the ask without its reload count. */
const requestOf = (tenantId: number | null, mode: TrafficQualityMode, request: QualityReportRequest): string =>
  JSON.stringify([tenantId, mode, request]);

/**
 * A read in flight, and the one ask it answers: its own, then the address choice it last rewrote the address to (so a
 * return to an earlier choice is a new read).
 */
type Read = { controller: AbortController; ask: string };

const sameHosts = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((h, i) => h === b[i]);

/**
 * One report request for the screen's filters while `enabled` (quality mode on for the brand). `setHosts` rewrites the
 * address's host choice when saved hosts are gone or refused. `attribution` is whether the source, link and campaign
 * filters apply (the hosts read says the environment credits a visit to them); null while that is not known for filters
 * that name one, and the report asks nothing until it is.
 *
 * A saved choice is first kept to the hosts a fresh hosts read and the latest answer still give the brand (never
 * widened to the default); a `host_not_owned` answer drops the hosts it names (or, naming none, reads the hosts again)
 * and asks once more. The address rewrite either makes is the request already on its way, so it asks nothing again.
 * Only the newest read lands. A report is held only for the request it answered (a reload asks the same one): during
 * the busy retry, and after a failed read that can be asked again. Any other read in flight or failed shows no figure.
 * The last report of the brand and mode stays as `latest` for the address list; a brand switch or a mode flip keeps
 * nothing.
 */
export const useQualityReport = (
  tenantId: number | undefined,
  enabled: boolean,
  filters: WebsiteFilters,
  attribution: boolean | null,
  setHosts: (next: HostSelection) => void,
): QualityReportState => {
  const [nonce, setNonce] = React.useState(0);
  const [shown, setShown] = React.useState<Shown | null>(null);
  const setHostsNow = React.useRef(setHosts);
  setHostsNow.current = setHosts;
  const running = React.useRef<Read | null>(null);
  /** The last report that landed, with the brand and mode and the request it answered. */
  const last = React.useRef<{ scope: string; request: string; report: QualityReport } | null>(null);
  /** The brand and mode whose saved addresses this hook replaced with none because they were gone. */
  const goneFor = React.useRef<string | null>(null);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  const mode: TrafficQualityMode = enabled ? 'quality' : 'legacy';
  const compiled = enabled ? reportRequest(filters, attribution === true) : null;
  const ask = askOf(tenantId ?? null, mode, compiled !== null && attribution === null ? 'wait' : compiled, nonce);

  React.useEffect(() => {
    const prev = running.current;
    // The address now names what the running read already asks (it rewrote the address itself).
    if (prev && !prev.controller.signal.aborted && prev.ask === ask) return;
    prev?.controller.abort();
    running.current = null;
    const [tenant, askedMode, request, askedNonce] = JSON.parse(ask) as Ask;
    const scope = scopeOf(tenant, askedMode);
    if (last.current?.scope !== scope) last.current = null;
    if (goneFor.current !== scope) goneFor.current = null;
    if (tenant === null || askedMode !== 'quality') {
      // The state names the brand and mode the hook went idle for, so coming back never shows an answer from before.
      setShown({ view: IDLE, ask, scope });
      return;
    }
    // A read starts once the request is known; until then the render shows loading, with the last report's addresses.
    if (request === 'wait') return;

    const read: Read = { controller: new AbortController(), ask };
    running.current = read;
    const { signal } = read.controller;
    let answering = ask;
    /** The request on its way (an address rewrite changes it): the only one whose earlier answer may be held. */
    let asking = request === null ? null : requestOf(tenant, askedMode, request);
    const live = (): boolean => running.current === read && !signal.aborted;
    const show = (view: Omit<View, 'latest'>): void => {
      if (live()) setShown({ view: { ...view, latest: last.current?.report ?? null }, ask: answering, scope });
    };
    const heldReport = (): QualityReport | null =>
      last.current !== null && last.current.request === asking ? last.current.report : null;
    const loading = (retrying: boolean): void => {
      const report = retrying ? heldReport() : null;
      show({ status: 'loading', report, held: report !== null, error: null, noSelection: null });
    };
    const fail = (code: QualityErrorCode | null): void => {
      const error = qualityScreenState(code);
      // Only a read worth asking again keeps its figures: a refused or not set up one shows its notice alone.
      const report = error.kind === 'failed' ? heldReport() : null;
      show({ status: 'error', report, held: report !== null, error, noSelection: null });
    };
    const noSelection = (why: 'chosen' | 'stale'): void =>
      show({ status: 'no-selection', report: null, held: false, error: null, noSelection: why });

    /** The request with `next` as its addresses, the address rewritten to match; null when none is left. */
    const rewrite = (body: QualityReportRequest, next: HostSelection): QualityReportRequest | null => {
      if (next.kind !== 'some') {
        goneFor.current = scope;
        noSelection('stale');
        setHostsNow.current({ kind: 'none' });
        return null;
      }
      if (body.selectedHosts && sameHosts(next.hosts, body.selectedHosts)) return body;
      const sent = { ...body, selectedHosts: next.hosts };
      answering = askOf(tenant, askedMode, sent, askedNonce);
      asking = requestOf(tenant, askedMode, sent);
      read.ask = answering;
      loading(false);
      setHostsNow.current(next);
      return sent;
    };
    /** The saved choice kept to the hosts the brand still has; unchanged when the hosts cannot be read. */
    const kept = async (hosts: string[]): Promise<HostSelection> => {
      const catalogue = await fetchAnalyticsHosts(tenant, signal).catch(() => null);
      // An environment that is not set up lists no address; the report then says so itself.
      if (!catalogue?.configured) return { kind: 'some', hosts };
      return reconcileHosts({ kind: 'some', hosts }, catalogue, last.current?.report.hosts ?? null);
    };

    const run = async (): Promise<void> => {
      if (request === null) {
        noSelection(goneFor.current === scope ? 'stale' : 'chosen');
        return;
      }
      goneFor.current = null;
      loading(false);
      let body = request;
      if (body.selectedHosts) {
        const saved = await kept(body.selectedHosts);
        if (!live()) return;
        const next = rewrite(body, saved);
        if (next === null) return;
        body = next;
      }
      let refused = false;
      for (;;) {
        const asked = body;
        try {
          const answer = await enqueueQuality(
            tenant,
            'report',
            () => postTrafficQuality(tenant, asked, signal, { onRetry: () => loading(true) }),
            signal,
          );
          if (!live()) return;
          if (!('meta' in answer)) {
            show({ status: 'no-site', report: null, held: false, error: null, noSelection: null });
            return;
          }
          last.current = { scope, request: requestOf(tenant, askedMode, asked), report: answer };
          show({ status: 'ready', report: answer, held: false, error: null, noSelection: null });
          return;
        } catch (err) {
          if (!live()) return;
          const code = err instanceof QualityError ? err.code : null;
          if (code !== 'host_not_owned' || !asked.selectedHosts || refused) {
            // A second refusal in a row, or one for the default addresses, is the generic failure.
            fail(code === 'host_not_owned' ? null : code);
            return;
          }
          refused = true;
          const named = err instanceof QualityError ? err.hosts : null;
          const selected = asked.selectedHosts;
          const left: HostSelection = named
            ? { kind: 'some', hosts: selected.filter((h) => !named.includes(h)) }
            : await kept(selected);
          if (!live()) return;
          const next = rewrite(asked, left.kind === 'some' && !left.hosts.length ? { kind: 'none' } : left);
          if (next === null) return;
          body = next;
        }
      }
    };
    void run();
  }, [ask]);

  // The read above outlives an ask it adopted, so it is aborted here on unmount, or by the next read it does not answer.
  React.useEffect(
    () => () => {
      running.current?.controller.abort();
      running.current = null;
    },
    [],
  );

  if (tenantId === undefined || !enabled) return OFF;
  if (shown?.ask === ask) return { ...shown.view, reload };
  // Between a change and the read it starts: no figure answers the new ask yet; the brand and mode's last report still
  // lists the addresses.
  const latest = shown?.scope === scopeOf(tenantId, mode) ? shown.view.latest : null;
  return { ...IDLE, status: 'loading', latest, reload };
};
